import React, { useState, useEffect, useCallback } from 'react';
import { X, Play, Save, RotateCcw, FileText, ChevronDown, ChevronRight, Maximize2, Edit3, Check, RefreshCw } from 'lucide-react';

const PromptPlayground = ({
    isOpen,
    onClose,
    onRun,
    initialPrompt = '',
    availableModels = [],
    currentDocumentType = '기타',
    isProcessing = false,
    apiBaseUrl,
    templates = {},
    selectedTemplateId = '', // ⭐ 왼쪽 패널에서 선택한 템플릿 ID
    onTemplateUpdate = null // ⭐ 템플릿 업데이트 콜백 (부모에서 전달)
}) => {
    const [prompt, setPrompt] = useState(initialPrompt);
    const [selectedModel, setSelectedModel] = useState(availableModels[0] || 'gemini-2.5-flash');
    const [promptTemplates, setPromptTemplates] = useState(null);
    const [selectedTemplateKey, setSelectedTemplateKey] = useState('unified');
    const [isExpanded, setIsExpanded] = useState(true);

    // New states for Popup and Resize
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [editorHeight, setEditorHeight] = useState(180); // 프롬프트 높이 줄임
    const [isResizing, setIsResizing] = useState(false);

    // ⭐ 필드 에디터 관련 상태
    const [editableFields, setEditableFields] = useState([]); // 편집 가능한 필드 목록
    const [editableTableFields, setEditableTableFields] = useState([]); // 테이블 필드 목록
    const [hasChanges, setHasChanges] = useState(false); // 변경사항 여부
    const [isSaving, setIsSaving] = useState(false); // 저장 중 상태
    const [showFieldEditor, setShowFieldEditor] = useState(true); // 필드 에디터 표시 여부
    const [currentTemplate, setCurrentTemplate] = useState(null); // 현재 템플릿

    // 초기 프롬프트 로드
    useEffect(() => {
        if (initialPrompt) {
            setPrompt(initialPrompt);
        }
    }, [initialPrompt]);

    // ⭐ 프롬프트 생성 함수 (재사용 가능)
    const generatePromptFromFields = useCallback((templateName, singleFields, tableFields) => {
        let promptContent = `Extract data for ${templateName}...\n\n`;

        // 📋 Target Fields (Single) - JSON 스키마 형식
        if (singleFields.length > 0) {
            promptContent += `### Target Fields (Single)\n`;
            promptContent += `You MUST extract the following fields (JSON Keys). Map the document content to these specific keys:\n`;
            promptContent += JSON.stringify(singleFields.map(f => ({
                key: typeof f === 'string' ? f : f.key,
                type: f.type || 'string',
                description: f.description || '',
                ...(f.exampleValue ? { example: f.exampleValue } : {})
            })), null, 2) + `\n\n`;
        }

        // 📊 Target Fields (Table) - description + example 포함
        if (tableFields.length > 0) {
            promptContent += `### Target Fields (Table)\n`;
            promptContent += `Extract these table/array fields with the specified columns:\n\n`;
            tableFields.forEach(table => {
                const tableName = table.tableName || table.key || 'items';
                const columns = table.columns || [];
                promptContent += `**${tableName}** (Array of objects):\n`;
                columns.forEach(col => {
                    const colKey = typeof col === 'string' ? col : col.key;
                    const colDesc = col.description || colKey;
                    const colExample = col.exampleValue ? ` (예: ${col.exampleValue})` : '';
                    promptContent += `  - ${colKey}: ${colDesc}${colExample}\n`;
                });
                promptContent += `\n`;
            });
        }

        // 💡 User Examples - exampleValue가 있는 필드들만 예시로 표시
        const examples = {};
        singleFields.forEach(f => {
            if (f.exampleValue) {
                examples[typeof f === 'string' ? f : f.key] = f.exampleValue;
            }
        });
        if (Object.keys(examples).length > 0) {
            promptContent += `### User Examples\n`;
            promptContent += `Refer to these examples to understand the expected format for specific fields:\n`;
            promptContent += JSON.stringify(examples, null, 2) + `\n\n`;
        }

        // 📝 Output JSON Structure
        promptContent += `## JSON Output Structure\n`;
        promptContent += `Return a valid JSON object with the following structure:\n`;
        promptContent += "```json\n{\n";
        singleFields.forEach((f, i) => {
            const key = typeof f === 'string' ? f : f.key;
            const displayName = f.displayName ? ` // ${f.displayName}` : '';
            promptContent += `  "${key}": "..."${displayName}${i < singleFields.length - 1 || tableFields.length > 0 ? ',' : ''}\n`;
        });
        tableFields.forEach((table, i) => {
            const tableName = table.tableName || table.key || 'items';
            const columns = table.columns || [];
            promptContent += `  "${tableName}": [\n`;
            promptContent += `    { ${columns.map(c => `"${c.key || c}": "..."`).join(', ')} }\n`;
            promptContent += `  ]${i < tableFields.length - 1 ? '' : ''}\n`;
        });
        promptContent += "}\n```\n";

        return promptContent;
    }, []);

    // ⭐ 외부에서 템플릿 선택 시 필드 로드 + 프롬프트 생성
    useEffect(() => {
        if (selectedTemplateId && templates) {
            const template = Object.values(templates).find(t => t.id === selectedTemplateId);
            if (template) {
                // 드롭다운 동기화
                setSelectedTemplateKey(`template:${template.id}`);
                setCurrentTemplate(template);

                // 필드 복사 (deep copy for editing)
                const singleFields = (template.singleFields || template.fields || []).map(f => ({
                    ...f,
                    key: typeof f === 'string' ? f : f.key
                }));
                const tableFields = (template.tableFields || []).map(t => ({
                    ...t,
                    columns: (t.columns || []).map(c => ({
                        ...c,
                        key: typeof c === 'string' ? c : c.key
                    }))
                }));

                setEditableFields(singleFields);
                setEditableTableFields(tableFields);
                setHasChanges(false);

                // 프롬프트 생성
                const promptContent = generatePromptFromFields(template.name, singleFields, tableFields);
                setPrompt(template.customPrompt || promptContent);
            }
        }
    }, [selectedTemplateId, templates, generatePromptFromFields]);

    // ⭐ 필드 변경 시 프롬프트 자동 재생성
    const regeneratePrompt = useCallback(() => {
        if (currentTemplate) {
            const promptContent = generatePromptFromFields(currentTemplate.name, editableFields, editableTableFields);
            setPrompt(promptContent);
        }
    }, [currentTemplate, editableFields, editableTableFields, generatePromptFromFields]);

    // ⭐ 단일 필드 업데이트 핸들러
    const handleFieldChange = (index, field, value) => {
        const newFields = [...editableFields];
        newFields[index] = { ...newFields[index], [field]: value };
        setEditableFields(newFields);
        setHasChanges(true);
    };

    // ⭐ 테이블 컬럼 업데이트 핸들러
    const handleTableColumnChange = (tableIndex, colIndex, field, value) => {
        const newTableFields = [...editableTableFields];
        const newColumns = [...newTableFields[tableIndex].columns];
        newColumns[colIndex] = { ...newColumns[colIndex], [field]: value };
        newTableFields[tableIndex] = { ...newTableFields[tableIndex], columns: newColumns };
        setEditableTableFields(newTableFields);
        setHasChanges(true);
    };

    // ⭐ 템플릿 저장 핸들러
    const handleSaveTemplate = async () => {
        if (!currentTemplate) return;

        setIsSaving(true);
        try {
            const baseUrl = apiBaseUrl || 'http://localhost:3002';
            const updatedTemplate = {
                ...currentTemplate,
                singleFields: editableFields,
                tableFields: editableTableFields
            };

            const response = await fetch(`${baseUrl}/api/templates/${currentTemplate.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedTemplate)
            });

            if (response.ok) {
                setHasChanges(false);
                // 프롬프트 재생성
                regeneratePrompt();
                // 부모 컴포넌트에 알림
                if (onTemplateUpdate) {
                    onTemplateUpdate(updatedTemplate);
                }
                alert('템플릿이 저장되었습니다.');
            } else {
                throw new Error('저장 실패');
            }
        } catch (error) {
            console.error('템플릿 저장 실패:', error);
            alert('저장 중 오류가 발생했습니다.');
        } finally {
            setIsSaving(false);
        }
    };

    // 프롬프트 템플릿 가져오기
    useEffect(() => {
        const fetchPrompts = async () => {
            try {
                // apiBaseUrl이 없으면 기본값 사용 (또는 상대 경로)
                const baseUrl = apiBaseUrl || 'http://localhost:3002';
                const response = await fetch(`${baseUrl}/api/admin/prompts`);
                const data = await response.json();

                if (data) {
                    // API 응답 키 매핑
                    setPromptTemplates({
                        ...data,
                        typeSpecific: data.TYPE_SPECIFIC_PROMPTS,
                        unified: data.UNIFIED_EXTRACTION_PROMPT,
                        fastClassification: data.FAST_CLASSIFICATION_PROMPT
                    });
                }
            } catch (error) {
                console.error('Failed to fetch prompts:', error);
            }
        };

        if (isOpen) {
            fetchPrompts();
        }
    }, [isOpen]);

    // 현재 문서 타입에 맞는 템플릿 자동 선택 (초기 로드 시)
    useEffect(() => {
        if (promptTemplates && currentDocumentType && promptTemplates.typeSpecific && promptTemplates.typeSpecific[currentDocumentType]) {
            // 초기 로드 시에만 적용하거나, 명시적으로 변경될 때 적용
            if (!initialPrompt && !selectedTemplateId) {
                setSelectedTemplateKey(`type:${currentDocumentType}`);
                setPrompt(promptTemplates.typeSpecific[currentDocumentType]);
            }
        }
    }, [promptTemplates, initialPrompt, selectedTemplateId]); // selectedTemplateId 추가

    // 문서 타입이 변경되면 프롬프트 업데이트 (단, 외부 템플릿 선택이 없을 때만)
    useEffect(() => {
        if (!selectedTemplateId && promptTemplates && currentDocumentType && promptTemplates.typeSpecific && promptTemplates.typeSpecific[currentDocumentType]) {
            console.log(`[PromptPlayground] Document type changed to: ${currentDocumentType}`);
            setSelectedTemplateKey(`type:${currentDocumentType}`);
            // 문서 타입이 바뀌면 항상 해당 타입의 템플릿으로 변경 (사용자 경험 향상)
            setPrompt(promptTemplates.typeSpecific[currentDocumentType]);
        }
    }, [currentDocumentType, promptTemplates, selectedTemplateId]);

    // 리사이징 핸들러
    const handleMouseDown = (e) => {
        e.preventDefault();
        setIsResizing(true);
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing) return;
            const container = document.getElementById('prompt-editor-container');
            if (!container) return;

            const newHeight = e.clientY - container.getBoundingClientRect().top;
            if (newHeight > 100 && newHeight < 800) {
                setEditorHeight(newHeight);
            }
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    const handleTemplateChange = (e) => {
        const key = e.target.value;
        setSelectedTemplateKey(key);

        if (!promptTemplates) return;

        if (key === 'unified') {
            setPrompt(promptTemplates.unified);
        } else if (key === 'fast') {
            setPrompt(promptTemplates.fastClassification);
        } else if (key.startsWith('type:')) {
            const type = key.split(':')[1];
            if (promptTemplates.typeSpecific && promptTemplates.typeSpecific[type]) {
                setPrompt(promptTemplates.typeSpecific[type]);
            }
        } else if (key.startsWith('template:')) {
            // ⭐ 동적 템플릿 선택 시 처리
            const templateId = key.split(':')[1];
            const template = Object.values(templates).find(t => t.id === templateId);
            if (template) {
                // 사용자 정의 프롬프트가 있으면 사용, 없으면 메타 프롬프트 또는 기본 프롬프트 사용
                setPrompt(template.customPrompt || `Extract data for ${template.name}...`);
            }
        }
    };

    const handleRun = () => {
        onRun(prompt, selectedModel);
    };

    // 팝업 모드용 렌더링 함수
    const renderControls = (isPopup = false) => (
        <div className={`px-2 py-0.5 bg-white border-b border-gray-200 flex items-center justify-between ${isPopup ? 'sticky top-0 z-10' : ''}`}>
            <div className="flex items-center space-x-2">
                {!isPopup && (
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="flex items-center text-xs text-gray-600 font-medium hover:text-gray-900 transition-colors"
                    >
                        {isExpanded ? <ChevronDown size={14} className="mr-0.5" /> : <ChevronRight size={14} className="mr-0.5" />}
                        Playground
                    </button>
                )}
                {isPopup && <span className="text-sm font-medium text-gray-800">Playground</span>}

                <div className="h-3 w-px bg-gray-300"></div>

                {/* 실행 버튼 - AI Studio 스타일 */}
                <button
                    onClick={handleRun}
                    disabled={isProcessing}
                    className={`flex items-center px-1.5 py-0.5 rounded text-xs ${isProcessing
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-600 hover:bg-gray-100 active:bg-gray-200'
                        }`}
                >
                    {isProcessing ? (
                        <>
                            <div className="animate-spin rounded-full h-3 w-3 border border-gray-400 border-t-transparent mr-1"></div>
                            <span>실행 중...</span>
                        </>
                    ) : (
                        <>
                            <Play size={12} className="mr-0.5 fill-current" />
                            <span>Run</span>
                        </>
                    )}
                </button>
            </div>

            <div className="flex items-center space-x-2">
                {/* 템플릿 선택 */}
                <div className="relative">
                    <select
                        value={selectedTemplateKey}
                        onChange={handleTemplateChange}
                        className="appearance-none pl-2 pr-6 py-0.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                        <option value="unified">전체 공통 (기본)</option>
                        <option value="fast">빠른 분류</option>

                        {/* ⭐ 동적 템플릿 목록 (최신 템플릿) */}
                        {templates && Object.values(templates).length > 0 && (
                            <optgroup label="사용자 정의 템플릿">
                                {Object.values(templates).map(template => (
                                    <option key={`template:${template.id}`} value={`template:${template.id}`}>
                                        {template.name}
                                    </option>
                                ))}
                            </optgroup>
                        )}

                        {/* 기존 시스템 템플릿 (레거시) */}
                        {promptTemplates?.typeSpecific && (
                            <optgroup label="시스템 템플릿">
                                {Object.keys(promptTemplates.typeSpecific).map(type => (
                                    <option key={`type:${type}`} value={`type:${type}`}>
                                        {type} 전용
                                    </option>
                                ))}
                            </optgroup>
                        )}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1 text-gray-400">
                        <ChevronDown size={12} />
                    </div>
                </div>

                {/* 모델 선택 */}
                <div className="relative">
                    <select
                        value={selectedModel}
                        onChange={(e) => setSelectedModel(e.target.value)}
                        className="appearance-none pl-2 pr-6 py-0.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                        {availableModels.map(model => (
                            <option key={model} value={model}>
                                {model === 'gemini-2.5-flash' ? 'Gemini 2.5 Flash' :
                                    model === 'gemini-2.5-pro' ? 'Gemini 2.5 Pro' :
                                        model === 'gemini-3-pro-preview' ? 'Gemini 3.0 Pro' :
                                            model}
                            </option>
                        ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1 text-gray-400">
                        <ChevronDown size={12} />
                    </div>
                </div>

                {/* 팝업 토글 버튼 */}
                {!isPopup && (
                    <button
                        onClick={() => setIsPopupOpen(true)}
                        className="text-gray-500 hover:text-blue-600 p-1.5 rounded-md hover:bg-blue-50 transition-colors"
                        title="크게 보기 (팝업)"
                    >
                        <Maximize2 size={18} />
                    </button>
                )}

                {isPopup ? (
                    <button onClick={() => setIsPopupOpen(false)} className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-200 transition-colors">
                        <X size={24} />
                    </button>
                ) : (
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-200 transition-colors">
                        <X size={20} />
                    </button>
                )}
            </div>
        </div>
    );

    if (!isOpen) return null;

    return (
        <>
            {/* 인라인 모드 */}
            <div className="bg-white border-b border-gray-200 shadow-sm" id="prompt-editor-container">
                {renderControls(false)}

                {/* 에디터 영역 (접기/펼치기) */}
                {isExpanded && (
                    <div className="relative transition-all duration-300 ease-in-out">
                        <textarea
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            style={{ height: `${editorHeight}px` }}
                            className="w-full p-3 font-mono text-xs text-gray-800 bg-gray-50 focus:bg-white focus:outline-none resize-none border-b border-gray-200"
                            placeholder="프롬프트를 입력하세요..."
                            spellCheck="false"
                        />

                        {/* 리사이즈 핸들 */}
                        <div
                            className="absolute bottom-0 left-0 right-0 h-1.5 bg-gray-200 hover:bg-blue-400 cursor-ns-resize flex justify-center items-center group transition-colors"
                            onMouseDown={handleMouseDown}
                        >
                            <div className="w-10 h-1 bg-gray-300 group-hover:bg-white rounded-full"></div>
                        </div>

                        <div className="px-4 py-2 bg-gray-100 text-xs text-gray-500 flex justify-between items-center">
                            <span>Tip: '실행' 버튼을 누르면 위 프롬프트로 현재 파일을 다시 추출합니다.</span>
                            <span>Length: {prompt.length} chars</span>
                        </div>
                    </div>
                )}

                {/* ⭐ 필드 에디터 섹션 */}
                {isExpanded && showFieldEditor && currentTemplate && (
                    <div className="border-t border-gray-200">
                        {/* 필드 에디터 헤더 */}
                        <div className="px-3 py-2 bg-gradient-to-r from-blue-50 to-white flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Edit3 size={14} className="text-blue-600" />
                                <span className="text-xs font-medium text-gray-700">
                                    {currentTemplate.name} 필드 설정
                                </span>
                                <span className="text-xs text-gray-500">
                                    ({editableFields.length}개 단일 + {editableTableFields.reduce((acc, t) => acc + (t.columns?.length || 0), 0)}개 테이블 컬럼)
                                </span>
                                {hasChanges && (
                                    <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">
                                        변경됨
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={regeneratePrompt}
                                    className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                    title="프롬프트 재생성"
                                >
                                    <RefreshCw size={12} />
                                    갱신
                                </button>
                                <button
                                    onClick={handleSaveTemplate}
                                    disabled={!hasChanges || isSaving}
                                    className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${hasChanges
                                            ? 'bg-blue-500 text-white hover:bg-blue-600'
                                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        }`}
                                >
                                    {isSaving ? (
                                        <><RefreshCw size={12} className="animate-spin" /> 저장 중...</>
                                    ) : (
                                        <><Save size={12} /> 저장</>
                                    )}
                                </button>
                                <button
                                    onClick={() => setShowFieldEditor(false)}
                                    className="text-gray-400 hover:text-gray-600 p-1"
                                    title="필드 에디터 닫기"
                                >
                                    <ChevronDown size={14} />
                                </button>
                            </div>
                        </div>

                        {/* 필드 테이블 */}
                        <div className="max-h-[300px] overflow-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                        <th className="px-2 py-1.5 text-left font-medium text-gray-600 w-[20%]">필드명</th>
                                        <th className="px-2 py-1.5 text-left font-medium text-gray-600 w-[10%]">타입</th>
                                        <th className="px-2 py-1.5 text-left font-medium text-gray-600 w-[35%]">
                                            DESCRIPTION
                                        </th>
                                        <th className="px-2 py-1.5 text-left font-medium text-gray-600 w-[35%]">
                                            EXAMPLE
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* 단일 필드 */}
                                    {editableFields.map((field, index) => (
                                        <tr key={`single-${index}`} className="border-t border-gray-100 hover:bg-blue-50/30">
                                            <td className="px-2 py-1.5">
                                                <span className="font-mono text-blue-700">{field.key}</span>
                                                {field.displayName && (
                                                    <span className="ml-1 text-gray-400 text-[10px]">({field.displayName})</span>
                                                )}
                                            </td>
                                            <td className="px-2 py-1.5">
                                                <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                                                    {field.type || 'string'}
                                                </span>
                                            </td>
                                            <td className="px-2 py-1.5">
                                                <input
                                                    type="text"
                                                    value={field.description || ''}
                                                    onChange={(e) => handleFieldChange(index, 'description', e.target.value)}
                                                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none"
                                                    placeholder="필드 설명..."
                                                />
                                            </td>
                                            <td className="px-2 py-1.5">
                                                <input
                                                    type="text"
                                                    value={field.exampleValue || ''}
                                                    onChange={(e) => handleFieldChange(index, 'exampleValue', e.target.value)}
                                                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none font-mono"
                                                    placeholder="예시 값..."
                                                />
                                            </td>
                                        </tr>
                                    ))}

                                    {/* 테이블 필드 구분선 */}
                                    {editableTableFields.length > 0 && (
                                        <tr className="bg-gray-50">
                                            <td colSpan={4} className="px-2 py-1.5 font-medium text-gray-700 text-[10px]">
                                                📊 테이블 필드 (Table Columns)
                                            </td>
                                        </tr>
                                    )}

                                    {/* 테이블 컬럼 */}
                                    {editableTableFields.map((table, tableIndex) => (
                                        table.columns?.map((col, colIndex) => (
                                            <tr key={`table-${tableIndex}-${colIndex}`} className="border-t border-gray-100 hover:bg-gray-50/30">
                                                <td className="px-2 py-1.5">
                                                    <span className="font-mono text-gray-700">{col.key}</span>
                                                    <span className="ml-1 text-gray-400 text-[10px]">
                                                        ({table.tableName || 'items'})
                                                    </span>
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-700">
                                                        {col.type || 'string'}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <input
                                                        type="text"
                                                        value={col.description || ''}
                                                        onChange={(e) => handleTableColumnChange(tableIndex, colIndex, 'description', e.target.value)}
                                                        className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:border-gray-400 focus:ring-1 focus:ring-amber-100 outline-none"
                                                        placeholder="컬럼 설명..."
                                                    />
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <input
                                                        type="text"
                                                        value={col.exampleValue || ''}
                                                        onChange={(e) => handleTableColumnChange(tableIndex, colIndex, 'exampleValue', e.target.value)}
                                                        className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:border-gray-400 focus:ring-1 focus:ring-amber-100 outline-none font-mono"
                                                        placeholder="예시 값..."
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* 필드 에디터 열기 버튼 (닫혀있을 때) */}
                {isExpanded && !showFieldEditor && currentTemplate && (
                    <button
                        onClick={() => setShowFieldEditor(true)}
                        className="w-full px-3 py-1.5 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 flex items-center justify-center gap-1 border-t border-gray-200 transition-colors"
                    >
                        <ChevronRight size={14} />
                        필드 설정 열기 ({currentTemplate.name})
                    </button>
                )}
            </div>

            {/* 팝업 모드 (전체화면 오버레이) */}
            {isPopupOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
                        {renderControls(true)}
                        <div className="flex-1 p-0 relative">
                            <textarea
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                className="w-full h-full p-6 font-mono text-base text-gray-800 bg-white focus:outline-none resize-none"
                                placeholder="프롬프트를 입력하세요..."
                                spellCheck="false"
                            />
                        </div>
                        <div className="px-6 py-3 bg-gray-100 text-sm text-gray-500 flex justify-between items-center border-t border-gray-200">
                            <span>Tip: 팝업에서도 '실행' 버튼을 눌러 바로 테스트할 수 있습니다.</span>
                            <span>Length: {prompt.length} chars</span>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PromptPlayground;
